import type { LoggedMeal } from "./types";

/**
 * Counts consecutive days (ending today or yesterday) with at least one
 * logged meal. Yesterday is a grace day — a streak doesn't drop until the
 * user goes 2 full days without logging.
 *
 * IMPORTANT: dates are compared in the user's LOCAL timezone, not UTC.
 * Without this, a user logging at 8pm EDT on Sunday would see the meal as
 * "Monday" (UTC date) and their Sunday streak would silently break.
 */
export function computeStreak(meals: LoggedMeal[]): number {
  if (meals.length === 0) return 0;

  // Bucket each meal by its LOCAL calendar day. `m.logged_at` is an ISO
  // UTC timestamp; `new Date(...)` converts it to the user's local zone.
  const days = new Set<string>();
  for (const m of meals) {
    days.add(localIsoDate(new Date(m.logged_at)));
  }

  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);

  // If today has no logs, start from yesterday (grace day).
  if (!days.has(localIsoDate(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(localIsoDate(cursor))) return 0;
  }

  let streak = 0;
  while (days.has(localIsoDate(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/** YYYY-MM-DD in the local timezone — matches what the dashboard date nav uses. */
function localIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
